import { useTranslation } from "react-i18next";
import Select from "./Select";
import PropTypes from 'prop-types';
import { useMemo } from "react";
import { withComponentCapabilities } from "./capabilities/componentCapabilities";
import { materializeValue } from "../utils/lazyValue";

export const customCtrlDef = {
    toString: async ({ value, defaultValue, t }) => {
        value = await materializeValue(value);
        if (!value) {
            return defaultValue;
        }
        const options = [
            { label: t('female'), value: 'Female' },
            { label: t('male'), value: 'Male' },
            { label: t('nonbinary'), value: 'Non-Binary' },
            { label: t('genderX'), value: 'X' },
            { label: t('genderUnknown'), value: 'Unknown' }
        ];
        const option = options.find(o => o.value === value);
        return option?.label ?? value;
    },
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: ({ def, href, value, ...props }) => <GenderDropdown gender={value} href={href ?? def.href} {...props}></GenderDropdown>
}

/**
 * A dropdown used to select a gender for a person.
 * @component
 */
const GenderDropdown = withComponentCapabilities(({ id, gender, placeholder, disabled = false, required = false, hideLabel = false, readonly = false, onChange, href }) => {
    const { t } = useTranslation();
    const options = useMemo(() => [
        { label: t('female'), value: 'Female' },
        { label: t('male'), value: 'Male' },
        { label: t('nonbinary'), value: 'Non-Binary' },
        { label: t('genderX'), value: 'X' },
        { label: t('genderUnknown'), value: 'Unknown' }
    ], [t]);

    return <Select id={id} hideLabel={hideLabel} disabled={disabled} placeholder={placeholder ?? t('gender')} required={required} labelField="label" valueField="value" value={gender} readonly={readonly} onChange={onChange} options={options} href={href}>
    </Select>
}, {
    lazyProps: ['gender']
});

GenderDropdown.propTypes = {
    /**
     * The gender to be displayed by the control
     */
    gender: PropTypes.string,

    /**
     * Function to call when the value has been changed
     */
    onChange: PropTypes.func.isRequired,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * Render the control as inline text rather than as an input control
     */
    readonly: PropTypes.bool,

    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default GenderDropdown;